"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    mvHeading: string;
    mvSub: string;
    riskAxis: string;
    returnAxis: string;
    efficientFrontier: string;
    mostEfficient: string;
    sameRisk1: string;
    sameRisk2: string;
    mvTitle: string;
    mvDescription: string;
    scenarios: { title: string; subtitle: string }[];
    assets: string[];
    sensHeading: string;
    sensSub: string;
    slightChange: string;
    sensNote: string;
    sensTitle: string;
    sensDescription: string;
    blSteps: { title: string; items: string[]; note: string }[];
    blHeading: string;
    blSub: string;
    blFooter: string;
    blTitle: string;
    blDescription: string;
  }
> = {
  ja: {
    mvHeading: "同じリスクなら、より高いリターンを目指す",
    mvSub: "効率的フロンティア上の配分が、理論上もっとも効率がよい",
    riskAxis: "リスク（値動きの大きさ）→",
    returnAxis: "期待リターン →",
    efficientFrontier: "効率的フロンティア",
    mostEfficient: "このリスクで最も効率的な配分",
    sameRisk1: "同じリスクだが",
    sameRisk2: "リターンが低い",
    mvTitle: "平均分散最適化 — 効率のよい配分を探す",
    mvDescription:
      "さまざまな配分の中から、同じリスクならリターンが最も高い組み合わせ（効率的フロンティア）を見つけます。灰色の点が配分の候補、青い線がフロンティアです。",
    scenarios: [
      { title: "元の前提", subtitle: "期待リターンどおり" },
      { title: "少しだけ変更", subtitle: "リターンを 0.5% 調整" },
    ],
    assets: ["株式A", "株式B", "債券", "現金"],
    sensHeading: "前提が少し変わるだけで、結果が大きく動く",
    sensSub: "期待リターンを 0.5% 変えただけで、配分が極端に偏ることがある",
    slightChange: "わずかな変更",
    sensNote: "これが平均分散最適化の「前提に敏感すぎる」という弱点",
    sensTitle: "平均分散最適化は前提に敏感",
    sensDescription:
      "期待リターンの推定を少し変えただけで、ある銘柄に極端に偏った配分が出ることがあります。理論上は正しくても、実務ではそのまま使いにくい理由です。",
    blSteps: [
      { title: "市場の配分を土台に", items: ["時価総額の比率", "= 市場全体の見方"], note: "まず偏りのない出発点" },
      { title: "自分の見通しを加える", items: ["「米国株は少し強い」", "「債券は弱めかも」"], note: "控えめに反映する" },
      { title: "調整された配分", items: ["極端に偏らない", "現実的な比率に"], note: "土台 + 微調整 = 安定" },
    ],
    blHeading: "Black-Litterman — 市場の土台に見通しを少しだけ足す",
    blSub: "全部を自分の予想で決めないから、配分が飛びすぎにくい",
    blFooter: "機関投資家やロボアドバイザーで広く使われている理由は、この安定感にある",
    blTitle: "Black-Litterman は「土台 + 微調整」で配分を考える",
    blDescription:
      "まず市場全体の配分を出発点にし、そこに自分の見通しを少しだけ加えます。最初から全部を予想で決めないので、極端な偏りが出にくくなります。",
  },
  en: {
    mvHeading: "At the same risk, aim for a higher return",
    mvSub: "Allocations on the efficient frontier are, in theory, the most efficient",
    riskAxis: "Risk (size of movement) →",
    returnAxis: "Expected return →",
    efficientFrontier: "Efficient frontier",
    mostEfficient: "The most efficient mix at this level of risk",
    sameRisk1: "Same risk, but",
    sameRisk2: "a lower return",
    mvTitle: "Mean-variance optimisation — searching for efficient allocations",
    mvDescription:
      "Among many possible allocations, it finds the combination with the highest return at a given level of risk (the efficient frontier). Grey dots are candidate allocations; the blue line is the frontier.",
    scenarios: [
      { title: "Original assumptions", subtitle: "As estimated" },
      { title: "A slight change", subtitle: "Return adjusted by 0.5%" },
    ],
    assets: ["Equity A", "Equity B", "Bonds", "Cash"],
    sensHeading: "A small change in assumptions moves the result a great deal",
    sensSub: "Change the expected return by just 0.5% and the mix can tilt to an extreme",
    slightChange: "A slight change",
    sensNote: "This is mean-variance optimisation's weakness: it is too sensitive to its assumptions",
    sensTitle: "Mean-variance optimisation is sensitive to assumptions",
    sensDescription:
      "A slight change to your expected-return estimate can produce a mix concentrated in one holding. That is why, correct as it is in theory, it is hard to use as-is in practice.",
    blSteps: [
      {
        title: "Start from the market allocation",
        items: ["Market-cap weights", "= the market's own view"],
        note: "An unbiased starting point",
      },
      {
        title: "Add your own outlook",
        items: ["“US equities look a little strong”", "“Bonds may be weak”"],
        note: "Reflected modestly",
      },
      {
        title: "An adjusted allocation",
        items: ["No extreme tilts", "Realistic weights"],
        note: "Base + fine-tuning = stability",
      },
    ],
    blHeading: "Black-Litterman — adding a little outlook on top of the market base",
    blSub: "Because you do not decide everything from your own forecast, the mix is less likely to jump",
    blFooter: "This stability is why it is widely used by institutional investors and robo-advisers",
    blTitle: "Black-Litterman builds an allocation from a base plus fine-tuning",
    blDescription:
      "Start from the whole market's allocation, then add a little of your own outlook. Because you do not decide everything by forecast from the outset, extreme tilts are less likely.",
  },
};

// ── Colors ────────────────────────────────────────────────────────

const C = {
  frontier: COLOR.sky,
  point: COLOR.emerald,
  warn: COLOR.rose,
  bl: COLOR.amber,
  accent: "#7c3aed",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 1: What mean-variance optimization does (conceptual)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function MeanVarianceConceptIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 240;

  // Chart area
  const pad = { top: 50, right: 30, bottom: 40, left: 50 };
  const cw = VW - pad.left - pad.right;
  const ch = VH - pad.top - pad.bottom;

  function toX(risk: number) {
    return pad.left + (risk / 25) * cw;
  }
  function toY(ret: number) {
    return pad.top + ((12 - ret) / 12) * ch;
  }

  // Frontier curve points
  const frontier: { x: number; y: number }[] = [];
  for (let r = 4; r <= 22; r += 0.5) {
    const ret = 1.5 + 2.2 * Math.sqrt(r - 3.5);
    frontier.push({ x: toX(r), y: toY(ret) });
  }
  const frontierPath = frontier.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Scattered candidate points (below frontier)
  const candidates = [
    { risk: 6, ret: 3.5 },
    { risk: 8, ret: 4.2 },
    { risk: 10, ret: 5.8 },
    { risk: 12, ret: 5.0 },
    { risk: 14, ret: 6.2 },
    { risk: 16, ret: 7.5 },
    { risk: 18, ret: 6.8 },
    { risk: 9, ret: 3.0 },
    { risk: 15, ret: 5.5 },
    { risk: 20, ret: 8.0 },
    { risk: 7, ret: 2.8 },
    { risk: 11, ret: 4.5 },
  ];

  // A highlighted "efficient" point on the frontier
  const efficientRisk = 12;
  const efficientRet = 1.5 + 2.2 * Math.sqrt(efficientRisk - 3.5);

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.mvHeading}
      </text>
      <text x={VW / 2} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.mvSub}
      </text>

      {/* Axes */}
      <line
        x1={pad.left}
        y1={pad.top}
        x2={pad.left}
        y2={VH - pad.bottom}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.15}
      />
      <line
        x1={pad.left}
        y1={VH - pad.bottom}
        x2={VW - pad.right}
        y2={VH - pad.bottom}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.15}
      />

      {/* Axis labels */}
      <text x={VW / 2} y={VH - 8} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.riskAxis}
      </text>
      <text
        x={14}
        y={pad.top + ch / 2}
        textAnchor="middle"
        fontSize="8"
        fill="currentColor"
        fillOpacity={0.4}
        transform={`rotate(-90, 14, ${pad.top + ch / 2})`}
      >
        {t.returnAxis}
      </text>

      {/* Grid lines */}
      {[3, 6, 9].map((ret) => (
        <line
          key={ret}
          x1={pad.left}
          y1={toY(ret)}
          x2={VW - pad.right}
          y2={toY(ret)}
          stroke="currentColor"
          strokeOpacity={0.06}
          strokeDasharray="3 6"
        />
      ))}

      {/* Candidate points */}
      {candidates.map((c, i) => (
        <circle
          key={i}
          cx={toX(c.risk)}
          cy={toY(c.ret)}
          r={3}
          fill="currentColor"
          fillOpacity={0.12}
          stroke="currentColor"
          strokeWidth="0.5"
          strokeOpacity={0.2}
        />
      ))}

      {/* Frontier curve */}
      <path d={frontierPath} fill="none" stroke={C.frontier} strokeWidth="2.5" strokeOpacity={0.8} />

      {/* Frontier area fill */}
      <path
        d={`${frontierPath} L ${frontier[frontier.length - 1].x} ${VH - pad.bottom} L ${frontier[0].x} ${VH - pad.bottom} Z`}
        fill={C.frontier}
        fillOpacity={0.04}
      />

      {/* Frontier label */}
      <text
        x={toX(20) + 4}
        y={toY(1.5 + 2.2 * Math.sqrt(20 - 3.5)) - 8}
        fontSize="8"
        fill={C.frontier}
        fontWeight="600"
      >
        {t.efficientFrontier}
      </text>

      {/* Highlighted efficient point */}
      <circle cx={toX(efficientRisk)} cy={toY(efficientRet)} r={5} fill="white" stroke={C.point} strokeWidth="2" />
      <line
        x1={toX(efficientRisk)}
        y1={toY(efficientRet) + 8}
        x2={toX(efficientRisk)}
        y2={VH - pad.bottom}
        stroke={C.point}
        strokeWidth="0.8"
        strokeDasharray="3 3"
        strokeOpacity={0.3}
      />

      {/* Annotation for efficient point */}
      <text x={toX(efficientRisk) + 10} y={toY(efficientRet) - 4} fontSize="7.5" fill={C.point} fontWeight="600">
        {t.mostEfficient}
      </text>

      {/* A suboptimal point below */}
      <circle cx={toX(12)} cy={toY(5.0)} r={3.5} fill="white" stroke={C.warn} strokeWidth="1.5" />
      <text x={toX(12) + 8} y={toY(5.0) + 3} fontSize="7" fill={C.warn} fontWeight="500">
        {t.sameRisk1}
      </text>
      <text x={toX(12) + 8} y={toY(5.0) + 12} fontSize="7" fill={C.warn} fontWeight="500">
        {t.sameRisk2}
      </text>

      {/* Vertical arrow between the two points */}
      <line
        x1={toX(12) - 8}
        y1={toY(5.0) - 4}
        x2={toX(12) - 8}
        y2={toY(efficientRet) + 6}
        stroke={C.point}
        strokeWidth="1"
        strokeOpacity={0.4}
      />
      <path
        d={`M ${toX(12) - 11} ${toY(efficientRet) + 10} L ${toX(12) - 8} ${toY(efficientRet) + 5} L ${toX(12) - 5} ${toY(efficientRet) + 10}`}
        fill="none"
        stroke={C.point}
        strokeWidth="1"
        strokeOpacity={0.4}
      />
    </svg>
  );
}

export function DocMeanVarianceConceptDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.mvTitle}
      description={t.mvDescription}
    >
      <MeanVarianceConceptIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 2: MV sensitivity problem
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const ASSET_COLORS = [C.frontier, C.point, C.bl, C.accent];

// Scenario 1: balanced result
const ALLOC_1 = [35, 30, 25, 10];
// Scenario 2: extreme swing from tiny input change
const ALLOC_2 = [70, 5, 20, 5];

function SensitivityIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 200;
  const cx = VW / 2;

  const colW = 160;
  const gap = 50;
  const leftCX = cx - colW / 2 - gap / 2;
  const rightCX = cx + colW / 2 + gap / 2;

  const barY = 64;
  const barH = 24;
  const barTotalW = colW;

  function AllocBars({ colCX, allocs }: { colCX: number; allocs: number[] }) {
    const startX = colCX - barTotalW / 2;
    let offset = 0;
    return (
      <g>
        {allocs.map((pct, i) => {
          const w = (pct / 100) * barTotalW;
          const bx = startX + offset;
          offset += w;
          return (
            <g key={t.assets[i]}>
              <rect
                x={bx}
                y={barY}
                width={Math.max(w, 0)}
                height={barH}
                rx={3}
                fill={ASSET_COLORS[i]}
                fillOpacity={0.2}
                stroke={ASSET_COLORS[i]}
                strokeWidth="0.8"
                strokeOpacity={0.5}
              />
              {w > 18 ? (
                <text
                  x={bx + w / 2}
                  y={barY + barH / 2 + 3}
                  textAnchor="middle"
                  fontSize="7.5"
                  fill={ASSET_COLORS[i]}
                  fontWeight="600"
                >
                  {pct}%
                </text>
              ) : null}
            </g>
          );
        })}
      </g>
    );
  }

  // Detail rows
  function DetailRows({ colCX, allocs }: { colCX: number; allocs: number[] }) {
    return (
      <g>
        {allocs.map((pct, i) => {
          const ry = barY + barH + 14 + i * 18;
          return (
            <g key={t.assets[i]}>
              <rect
                x={colCX - colW / 2 + 4}
                y={ry}
                width={8}
                height={8}
                rx={2}
                fill={ASSET_COLORS[i]}
                fillOpacity={0.3}
              />
              <text x={colCX - colW / 2 + 16} y={ry + 7} fontSize="7.5" fill={ASSET_COLORS[i]} fontWeight="500">
                {t.assets[i]}
              </text>
              <text
                x={colCX + colW / 2 - 4}
                y={ry + 7}
                textAnchor="end"
                fontSize="8"
                fill={ASSET_COLORS[i]}
                fontWeight="700"
              >
                {pct}%
              </text>
            </g>
          );
        })}
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.sensHeading}
      </text>
      <text x={cx} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.sensSub}
      </text>

      {/* Left scenario */}
      <text x={leftCX} y={barY - 14} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="600">
        {t.scenarios[0].title}
      </text>
      <text x={leftCX} y={barY - 4} textAnchor="middle" fontSize="7" fill="currentColor" fillOpacity={0.45}>
        {t.scenarios[0].subtitle}
      </text>
      <AllocBars colCX={leftCX} allocs={ALLOC_1} />
      <DetailRows colCX={leftCX} allocs={ALLOC_1} />

      {/* Arrow */}
      <defs>
        <marker id="poSensArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke={C.warn} strokeWidth="1" strokeOpacity={0.5} />
        </marker>
      </defs>
      <line
        x1={leftCX + colW / 2 + 6}
        y1={barY + barH / 2}
        x2={rightCX - colW / 2 - 6}
        y2={barY + barH / 2}
        stroke={C.warn}
        strokeWidth="1.2"
        strokeOpacity={0.3}
        markerEnd="url(#poSensArrow)"
      />
      <text x={cx} y={barY + barH / 2 - 6} textAnchor="middle" fontSize="7.5" fill={C.warn} fontWeight="600">
        {t.slightChange}
      </text>

      {/* Right scenario */}
      <text x={rightCX} y={barY - 14} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="600">
        {t.scenarios[1].title}
      </text>
      <text x={rightCX} y={barY - 4} textAnchor="middle" fontSize="7" fill="currentColor" fillOpacity={0.45}>
        {t.scenarios[1].subtitle}
      </text>
      <AllocBars colCX={rightCX} allocs={ALLOC_2} />
      <DetailRows colCX={rightCX} allocs={ALLOC_2} />

      {/* Bottom note */}
      <text x={cx} y={VH - 6} textAnchor="middle" fontSize="8" fill={C.warn} fontWeight="600">
        {t.sensNote}
      </text>
    </svg>
  );
}

export function DocMVSensitivityConceptDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.sensTitle}
      description={t.sensDescription}
    >
      <SensitivityIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 3: BL approach — market baseline + views
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function BlackLittermanConceptIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 220;
  const cx = VW / 2;

  // Three-step flow
  const stepW = 120;
  const stepH = 100;
  const gap = 24;
  const totalW = 3 * stepW + 2 * gap;
  const startX = (VW - totalW) / 2;
  const stepY = 56;

  const stepColors = [C.frontier, C.bl, C.point];
  const steps = t.blSteps.map((step, i) => ({ ...step, color: stepColors[i]! }));

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={cx} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.blHeading}
      </text>
      <text x={cx} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.blSub}
      </text>

      {/* Arrow marker */}
      <defs>
        <marker id="poBLArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.25} />
        </marker>
      </defs>

      {steps.map((step, i) => {
        const sx = startX + i * (stepW + gap);
        const sCX = sx + stepW / 2;

        return (
          <g key={step.title}>
            {/* Step box */}
            <rect
              x={sx}
              y={stepY}
              width={stepW}
              height={stepH}
              rx={8}
              fill={step.color}
              fillOpacity={0.06}
              stroke={step.color}
              strokeWidth="1"
              strokeOpacity={0.3}
            />

            {/* Step number */}
            <circle
              cx={sCX}
              cy={stepY + 16}
              r={9}
              fill={step.color}
              fillOpacity={0.12}
              stroke={step.color}
              strokeWidth="0.8"
              strokeOpacity={0.4}
            />
            <text x={sCX} y={stepY + 19.5} textAnchor="middle" fontSize="9" fill={step.color} fontWeight="700">
              {i + 1}
            </text>

            {/* Title */}
            <text x={sCX} y={stepY + 38} textAnchor="middle" fontSize="8.5" fill={step.color} fontWeight="600">
              {step.title}
            </text>

            {/* Items */}
            {step.items.map((item, j) => (
              <text
                key={j}
                x={sCX}
                y={stepY + 54 + j * 12}
                textAnchor="middle"
                fontSize="7"
                fill={step.color}
                fillOpacity={0.6}
              >
                {item}
              </text>
            ))}

            {/* Note below */}
            <text x={sCX} y={stepY + stepH + 14} textAnchor="middle" fontSize="7.5" fill={step.color} fillOpacity={0.5}>
              {step.note}
            </text>

            {/* Arrow to next */}
            {i < 2 ? (
              <line
                x1={sx + stepW + 4}
                y1={stepY + stepH / 2}
                x2={sx + stepW + gap - 4}
                y2={stepY + stepH / 2}
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity={0.15}
                markerEnd="url(#poBLArrow)"
              />
            ) : null}
          </g>
        );
      })}

      {/* Bottom note */}
      <text x={cx} y={VH - 6} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.blFooter}
      </text>
    </svg>
  );
}

export function DocBlackLittermanConceptDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.blTitle}
      description={t.blDescription}
    >
      <BlackLittermanConceptIllustration />
    </IllustrationCard>
  );
}
